import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, TextField } from "@mui/material";
import * as Styles from "./SupportCases.styles.ts";

interface EscalateCaseDialogProps {
	isLoading: boolean;
	open: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => Promise<void>;
}

const EscalateCaseDialog = ({ isLoading, open, onClose, onConfirm }: EscalateCaseDialogProps) => {
	const [reason, setReason] = useState("");
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [hasRequestError, setHasRequestError] = useState(false);
	const trimmedReason = reason.trim();

	useEffect(() => {
		if (!open) {
			setReason("");
			setHasSubmitted(false);
			setHasRequestError(false);
		}
	}, [open]);

	const handleConfirm = async () => {
		setHasSubmitted(true);
		setHasRequestError(false);

		if (!trimmedReason) return;

		try {
			await onConfirm(trimmedReason);
		} catch {
			setHasRequestError(true);
		}
	};

	return (
		<Styles.EscalateDialog
			aria-labelledby="escalate-case-dialog-title"
			maxWidth={false}
			onClose={isLoading ? undefined : onClose}
			open={open}
		>
			<Styles.EscalateDialogTitle id="escalate-case-dialog-title">
				Escalate Case
				<Styles.EscalateDialogCloseButton
					aria-label="Close escalate case dialog"
					disabled={isLoading}
					onClick={onClose}
				>
					<CloseIcon />
				</Styles.EscalateDialogCloseButton>
			</Styles.EscalateDialogTitle>

			<Styles.EscalateDialogContent>
				<p>Please explain why this case is being escalated so we can help resolve the issue.</p>

				{hasRequestError && <Alert severity="error">Unable to escalate this case. Please try again.</Alert>}

				<TextField
					autoFocus
					disabled={isLoading}
					error={hasSubmitted && !trimmedReason}
					helperText={hasSubmitted && !trimmedReason ? "Escalation reason is required." : undefined}
					label="Escalation Reason"
					onChange={(event) => setReason(event.target.value)}
					required
					size="small"
					value={reason}
				/>
			</Styles.EscalateDialogContent>

			<Styles.EscalateDialogActions>
				<Styles.EscalateDialogActionButton
					disabled={isLoading}
					onClick={onClose}
					variant="outlined"
				>
					Cancel
				</Styles.EscalateDialogActionButton>
				<Styles.EscalateDialogActionButton
					disabled={isLoading}
					onClick={() => void handleConfirm()}
					variant="contained"
				>
					{isLoading ? "Escalating..." : "Escalate Case"}
				</Styles.EscalateDialogActionButton>
			</Styles.EscalateDialogActions>
		</Styles.EscalateDialog>
	);
};

export default EscalateCaseDialog;
